import React, { useState, useEffect } from 'react';
import { useArticles } from '../../api/ArticleContext2';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import {
  Card,
  Row,
  Col,
  Button,
  Input,
  Select,
  Tag,
  Typography,
  Space,
  Divider,
  message,
  Tabs,
  Alert,
  Statistic,
  Avatar,
  Grid,
  Pagination
} from 'antd';
import {
  EditOutlined,
  EyeOutlined,
  SaveOutlined,
  SendOutlined,
  CalendarOutlined,
  UserOutlined,
  TagOutlined,
  FolderOutlined,
  ClockCircleOutlined,
  CodeOutlined,
  LinkOutlined,
  PictureOutlined,
  UnorderedListOutlined
} from '@ant-design/icons';
import './WriteArticle.css';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;
const { Option } = Select;
const { useBreakpoint } = Grid;

const WriteArticle = () => {
  const { publishArticle, updateArticle, getArticleById } = useArticles();
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const screens = useBreakpoint();
  
  const isEditing = !!id;
  const currentArticle = isEditing ? getArticleById(parseInt(id)) : null;
  
  const [article, setArticle] = useState({
    title: '',
    author: '',
    excerpt: '',
    content: '',
    tags: '',
    category: '前端'
  });

  const [activeTab, setActiveTab] = useState('edit');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [autoSaveStatus, setAutoSaveStatus] = useState('已保存');

  // 检查用户是否登录
  const isAuthenticated = () => {
    return !!localStorage.getItem('blog_current_user');
  };

  // 获取当前用户
  const getCurrentUser = () => {
    const user = localStorage.getItem('blog_current_user');
    return user ? JSON.parse(user) : null;
  };

  // 如果是编辑模式，加载文章数据
  useEffect(() => {
    // 检查登录状态
    if (!isAuthenticated()) {
      message.warning('请先登录才能发布文章');
      navigate('/auth', { state: { from: location.pathname } });
      return;
    }

    const currentUser = getCurrentUser();
    if (isEditing && currentArticle) {
      setArticle({
        title: currentArticle.title || '',
        author: currentArticle.author || currentUser?.username || '',
        excerpt: currentArticle.excerpt || '',
        content: currentArticle.content || '',
        tags: currentArticle.tags ? currentArticle.tags.join(',') : '',
        category: currentArticle.category || '前端'
      });
    } else {
      // 新文章设置作者为当前用户
      setArticle(prev => ({
        ...prev,
        author: currentUser?.username || ''
      }));
    }
  }, [isEditing, currentArticle, navigate, location]);

  const handleChange = (name, value) => {
    setArticle(prev => ({
      ...prev,
      [name]: value
    }));
    
    // 模拟自动保存状态
    setAutoSaveStatus('保存中...');
    setTimeout(() => setAutoSaveStatus('已保存'), 1000);
  };

  const handleSubmit = async () => {
    // 检查登录状态
    if (!isAuthenticated()) {
      message.error('请先登录才能发布文章');
      navigate('/auth', { state: { from: location.pathname } });
      return;
    }

    // 验证必填字段
    if (!article.title.trim()) {
      message.error('请填写文章标题');
      return;
    }
    
    if (!article.excerpt.trim()) {
      message.error('请填写文章摘要');
      return;
    }
    
    if (!article.content.trim()) {
      message.error('请填写文章内容');
      return;
    }

    setIsSubmitting(true);

    try {
      if (isEditing) {
        // 更新文章
        await updateArticle(parseInt(id), {
          ...article,
          tags: article.tags ? article.tags.split(',').map(tag => tag.trim()).filter(tag => tag) : ['未分类']
        });
        
        message.success('文章更新成功！');
        
        // 2秒后跳转到文章详情页
        setTimeout(() => {
          navigate(`/article/${id}`);
        }, 2000);
      } else {
        // 发布新文章
        const newArticle = await publishArticle(article);
        
        message.success('文章发布成功！');
        
        // 清空表单
        setArticle({
          title: '',
          author: getCurrentUser()?.username || '',
          excerpt: '',
          content: '',
          tags: '',
          category: '前端'
        });

        // 2秒后跳转到首页
        setTimeout(() => {
          navigate('/');
        }, 2000);
      }

    } catch (error) {
      message.error(isEditing ? '更新失败，请重试' : '发布失败，请重试');
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatDate = () => {
    const now = new Date();
    return now.toLocaleDateString('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  };

  const handleInsert = (type) => {
    let insertText = '';

    switch (type) {
      case '标题':
        insertText = '# 这是标题\n\n';
        break;
      case '代码块':
        insertText = '```javascript\n// 在这里写代码\nconsole.log("Hello World");\n```\n\n';
        break;
      case '链接':
        insertText = '[链接文字](https://example.com)';
        break;
      case '图片':
        insertText = '![图片描述](https://via.placeholder.com/400x200)';
        break;
      case '列表':
        insertText = '- 列表项1\n- 列表项2\n- 列表项3\n\n';
        break;
      default:
        return;
    }

    const textarea = document.querySelector('.write-content-textarea');
    if (textarea) {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const text = textarea.value;
      
      const newText = text.substring(0, start) + insertText + text.substring(end);
      handleChange('content', newText);
    }
  };

  const tabItems = [
    {
      key: 'edit',
      label: (
        <span>
          <EditOutlined />
          编辑
        </span>
      ),
      children: (
        <Row gutter={[24, 24]}>
          <Col span={24}>
            <Card bordered={false} className="write-content-card">
              <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                {/* 标题 */}
                <div>
                  <Title level={4} style={{ marginBottom: 8 }}>文章标题</Title>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    编辑人：{article.author} • 上次编辑：{formatDate()}
                  </Text>
                  <Input
                    size="large"
                    value={article.title}
                    onChange={(e) => handleChange('title', e.target.value)}
                    placeholder="请输入文章标题"
                    style={{ marginTop: 12 }}
                  />
                </div>

                {/* 摘要 */}
                <div>
                  <Title level={4} style={{ marginBottom: 8 }}>文章摘要</Title>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    编辑人：{article.author} • 建议字数：100-200字
                  </Text>
                  <TextArea
                    value={article.excerpt}
                    onChange={(e) => handleChange('excerpt', e.target.value)}
                    placeholder="请输入文章摘要，用于在列表页显示"
                    rows={3}
                    style={{ marginTop: 12 }}
                  />
                </div>

                {/* 内容 */}
                <div>
                  <Title level={4} style={{ marginBottom: 8 }}>文章内容</Title>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    请使用Markdown格式输入文章内容 • 支持：标题、列表、代码块、链接等
                  </Text>
                  
                  <Space wrap style={{ marginTop: 12, marginBottom: 12 }}>
                    <Button 
                      size="small" 
                      icon={<UnorderedListOutlined />}
                      onClick={() => handleInsert('标题')}
                      className="write-tool-btn"
                    >
                      插入标题
                    </Button>
                    <Button 
                      size="small" 
                      icon={<CodeOutlined />}
                      onClick={() => handleInsert('代码块')}
                      className="write-tool-btn"
                    >
                      插入代码块
                    </Button>
                    <Button 
                      size="small" 
                      icon={<LinkOutlined />}
                      onClick={() => handleInsert('链接')}
                      className="write-tool-btn"
                    >
                      插入链接
                    </Button>
                    <Button 
                      size="small" 
                      icon={<PictureOutlined />}
                      onClick={() => handleInsert('图片')}
                      className="write-tool-btn"
                    >
                      插入图片
                    </Button>
                    <Button 
                      size="small" 
                      icon={<UnorderedListOutlined />}
                      onClick={() => handleInsert('列表')}
                      className="write-tool-btn"
                    >
                      插入列表
                    </Button>
                  </Space>
                  
                  <TextArea
                    className="write-content-textarea"
                    value={article.content}
                    onChange={(e) => handleChange('content', e.target.value)}
                    placeholder="请在此处输入文章内容（支持Markdown语法）..."
                    rows={12}
                  />
                </div>

                {/* 标签 */}
                <div>
                  <Title level={4} style={{ marginBottom: 8 }}>文章标签</Title>
                  <Input
                    value={article.tags}
                    onChange={(e) => handleChange('tags', e.target.value)}
                    placeholder="请输入标签，用逗号分隔（例如：React,JavaScript,前端）"
                  />
                </div>

                {/* 文章信息 */}
                <Row gutter={24}>
                  <Col span={12}>
                    <Title level={4} style={{ marginBottom: 8 }}>作者</Title>
                    <Input
                      value={article.author}
                      onChange={(e) => handleChange('author', e.target.value)}
                      placeholder="请输入作者姓名"
                    />
                  </Col>
                  <Col span={12}>
                    <Title level={4} style={{ marginBottom: 8 }}>分类</Title>
                    <Select
                      value={article.category}
                      onChange={(value) => handleChange('category', value)}
                      style={{ width: '100%' }}
                    >
                      <Option value="前端">前端</Option>
                      <Option value="后端">后端</Option>
                      <Option value="JavaScript">JavaScript</Option>
                      <Option value="React">React</Option>
                      <Option value="Node.js">Node.js</Option>
                      <Option value="TypeScript">TypeScript</Option>
                      <Option value="其他">其他</Option>
                    </Select>
                  </Col>
                </Row>
              </Space>
            </Card>
          </Col>
        </Row>
      )
    },
    {
      key: 'preview',
      label: (
        <span>
          <EyeOutlined />
          预览
        </span>
      ),
      children: (
        <Card bordered={false} className="write-content-card">
          <div className="write-header" style={{ textAlign: 'left', padding: '0 0 24px 0' }}>
            <Title level={2} style={{ marginBottom: 8 }}>{article.title || '文章标题预览'}</Title>
            <Space size="middle">
              <Space size="small">
                <Avatar size="small" icon={<UserOutlined />} />
                <Text type="secondary">{article.author}</Text>
              </Space>
              <Space size="small">
                <CalendarOutlined />
                <Text type="secondary">{formatDate()}</Text>
              </Space>
              <Space size="small">
                <FolderOutlined />
                <Text type="secondary">{article.category}</Text>
              </Space>
            </Space>
          </div>
          
          <Divider />
          
          <div style={{ marginBottom: 24 }}>
            <Title level={4} style={{ marginBottom: 12 }}>文章摘要</Title>
            <Alert
              message={article.excerpt || '文章摘要预览内容...'}
              type="info"
              showIcon
            />
          </div>
          
          <div style={{ marginBottom: 24 }}>
            <Title level={4} style={{ marginBottom: 12 }}>文章内容</Title>
            <div className="write-preview">
              {article.content || '文章内容预览...\n\n## 二级标题\n\n这里是文章内容。\n\n```javascript\nconsole.log("Hello World");\n```\n\n- 列表项1\n- 列表项2\n- 列表项3'}
            </div>
          </div>
          
          <div>
            <Title level={4} style={{ marginBottom: 12 }}>文章标签</Title>
            <Space wrap>
              {article.tags ? (
                article.tags.split(',').map(tag => tag.trim()).filter(tag => tag).map(tag => (
                  <Tag key={tag} icon={<TagOutlined />}>{tag}</Tag>
                ))
              ) : (
                <Tag>示例标签</Tag>
              )}
            </Space>
          </div>
        </Card>
      )
    }
  ];

  return (
    <div className="write-container">
      {/* 头部区域 */}
      <div className="write-header" style={{ textAlign: 'left' }}>
        <Row justify="space-between" align="middle">
          <Col>
            <Title level={2} style={{ margin: 0 }}>
              <EditOutlined style={{ marginRight: 12, color: '#1890ff' }} />
              {isEditing ? '编辑文章' : '撰写新文章'}
            </Title>
            <Text type="secondary">
              当前日期: {formatDate()} • 自动保存: <Text type="success">{autoSaveStatus}</Text>
            </Text>
          </Col>
          <Col>
            <Space>
              <Button 
                icon={<SaveOutlined />}
                onClick={() => message.success('已保存到草稿箱')}
              >
                保存草稿
              </Button>
              <Button 
                type="primary" 
                icon={<SendOutlined />}
                loading={isSubmitting}
                onClick={handleSubmit}
              >
                {isEditing ? '更新文章' : '发布文章'}
              </Button>
            </Space>
          </Col>
        </Row>
      </div>

      <Row gutter={[24, 24]}>
        {/* 左侧信息面板 */}
        <Col xs={24} md={6}>
          <Space direction="vertical" style={{ width: '100%' }}>
            <Card 
              size="small" 
              title="撰写工具"
              bordered={false}
              className="write-filter-card"
            >
              <Tabs
                activeKey={activeTab}
                onChange={setActiveTab}
                items={[
                  {
                    key: 'edit',
                    label: '编辑',
                    children: null
                  },
                  {
                    key: 'preview',
                    label: '预览',
                    children: null
                  }
                ]}
              />
              
              <Divider />
              
              <Space direction="vertical" style={{ width: '100%' }}>
                <Button 
                  block 
                  type={activeTab === 'edit' ? 'primary' : 'default'}
                  onClick={() => setActiveTab('edit')}
                  icon={<EditOutlined />}
                >
                  编辑模式
                </Button>
                <Button 
                  block 
                  type={activeTab === 'preview' ? 'primary' : 'default'}
                  onClick={() => setActiveTab('preview')}
                  icon={<EyeOutlined />}
                >
                  预览模式
                </Button>
              </Space>
            </Card>

            <Card 
              size="small" 
              title="文章统计"
              bordered={false}
              className="write-filter-card"
            >
              <Space direction="vertical" style={{ width: '100%' }}>
                <Statistic 
                  title="标题字数" 
                  value={article.title.length}
                  valueStyle={{ fontSize: '24px' }}
                />
                <Statistic 
                  title="摘要字数" 
                  value={article.excerpt.length}
                  valueStyle={{ fontSize: '24px' }}
                />
                <Statistic 
                  title="内容字数" 
                  value={article.content.length}
                  valueStyle={{ fontSize: '24px' }}
                />
                <Statistic 
                  title="标签数量" 
                  value={article.tags ? article.tags.split(',').filter(tag => tag.trim()).length : 0}
                  valueStyle={{ fontSize: '24px' }}
                />
              </Space>
            </Card>

            <Card 
              size="small" 
              title="快捷操作"
              bordered={false}
              className="write-filter-card"
            >
              <Space direction="vertical" style={{ width: '100%' }}>
                <Button 
                  block 
                  icon={<SaveOutlined />}
                  onClick={() => message.success('已保存到草稿箱')}
                >
                  保存到草稿箱
                </Button>
                <Button 
                  block 
                  icon={<SendOutlined />} 
                  type="primary"
                  onClick={handleSubmit}
                  loading={isSubmitting}
                >
                  立即发布
                </Button>
                <Button 
                  block 
                  danger
                  onClick={() => navigate(-1)}
                >
                  放弃编辑
                </Button>
              </Space>
            </Card>
          </Space>
        </Col>

        {/* 主编辑区域 */}
        <Col xs={24} md={18}>
          <Tabs
            activeKey={activeTab}
            onChange={setActiveTab}
            items={tabItems}
          />
        </Col>
      </Row>
    </div>
  );
};

export default WriteArticle;